import { canDo, SignatureAction } from 'src/types/signatureContext';

/**
 * BRK-2073: gate + target logic for the document-on-behalf affordances (epic BRK-2055; backend =
 * BRK-2072), kept as pure functions so the enable matrix is testable without mounting the EoC
 * document tables. Sibling of compliance-sign-on-behalf.ts.
 *
 * A `COMPLIANCE_DOCUMENT_ON_BEHALF` holder unsigns and edits/resubmits another therapist's
 * documents — any discipline, so unlike the legacy gates there is deliberately NO
 * `document.type === discipline` check here. The backend computes the
 * `CAN_COMPLIANCE_DOCUMENT_ON_BEHALF` marker per row in lock-step with its own enforcement
 * (`ComplianceOnBehalfAccessPolicy.resolveDocumentVerbGrant`), and always pairs it with the
 * existing action it qualifies (CAN_UNSIGN / CAN_EDIT) on the same axis — so the affordance
 * never surfaces an action the server would refuse, and compliance mode never fires for a
 * natively-entitled actor (author / assigned supervisor), who gets the bare action instead.
 *
 * Every gate requires the epic's single kill switch (`provider_portal_compliance_on_behalf`,
 * fail-closed while loading) AND the scope AND the row marker.
 */

/** The IamScopeKey the backend mints (restore-core catalog v31). */
export const COMPLIANCE_DOCUMENT_ON_BEHALF_SCOPE = 'COMPLIANCE_DOCUMENT_ON_BEHALF';

export type ComplianceDocAxis = 'THERAPIST' | 'SUPERVISOR';

interface ComplianceDocGateArgs {
  complianceSplitOn: boolean | null;
  hasDocumentScope: boolean;
  document: any;
}

const axisHasMarkerWith = (actions: any, companion: SignatureAction): boolean =>
  canDo(actions, SignatureAction.CAN_COMPLIANCE_DOCUMENT_ON_BEHALF) && canDo(actions, companion);

const gatePasses = (args: {
  complianceSplitOn: boolean | null;
  hasDocumentScope: boolean;
}): boolean => args.complianceSplitOn === true && args.hasDocumentScope;

/**
 * The unsign fork: true when the current user's Undo Signature on this row is compliance-granted,
 * i.e. the marker rides CAN_UNSIGN on ANY axis (therapist, supervisor, or the physician-signed
 * lockdown's physician axis). Compliance mode swaps the plain confirmation for the mandatory
 * reason+consent modal and sends `onBehalfReason` on the unsign call.
 */
export function isComplianceUnsignMode(args: ComplianceDocGateArgs): boolean {
  if (!gatePasses(args)) {
    return false;
  }
  const ctx = args.document?.signatureContext;
  return (
    axisHasMarkerWith(ctx?.allowedTherapistSignatureActions, SignatureAction.CAN_UNSIGN) ||
    axisHasMarkerWith(ctx?.allowedSupervisorSignatureActions, SignatureAction.CAN_UNSIGN) ||
    axisHasMarkerWith(ctx?.allowedPhysicianSignatureActions, SignatureAction.CAN_UNSIGN)
  );
}

/**
 * The edit fork: therapist axis ONLY (K7 — supervisor-axis CAN_EDIT is the cosigner-edit fork
 * discriminator; the backend never emits the marker beside it, and this gate must never route a
 * holder into the supervisor edit flow). Compliance mode collects the mandatory reason before the
 * edit opens and carries it on the webhook mint.
 */
export function isComplianceEditMode(args: ComplianceDocGateArgs): boolean {
  if (!gatePasses(args)) {
    return false;
  }
  return axisHasMarkerWith(
    args.document?.signatureContext?.allowedTherapistSignatureActions,
    SignatureAction.CAN_EDIT
  );
}

/**
 * The create fork (BRK-2074): unlike unsign/edit there is no row yet, so no marker — the gate is
 * page-level: the single Split (fail-closed while loading) AND the scope. The discipline-mismatch
 * condition that decides WHEN a create affordance forks into on-behalf mode lives at the call
 * sites, beside the legacy `therapistDiscipline === activeTab` gates it relaxes — a
 * discipline-matched holder keeps the legacy self-create flow untouched.
 */
export function isComplianceCreateMode(args: {
  complianceSplitOn: boolean | null;
  hasDocumentScope: boolean;
}): boolean {
  return gatePasses(args);
}

/**
 * Whom the action is taken on behalf OF — drives the reason modal's identity card. Therapist-first
 * when both axes carry the marker (mirrors the sign module's axis precedence); a physician-axis-only
 * marker (lockdown rows) resolves to the author, since the unsign cascade unwinds to the author's
 * draft and the backend stamps the audit axis itself.
 */
export function getComplianceDocTarget(
  document: any
): { axis: ComplianceDocAxis; onBehalfOfProviderId: string | null } | null {
  const ctx = document?.signatureContext;
  const marker = SignatureAction.CAN_COMPLIANCE_DOCUMENT_ON_BEHALF;
  if (
    canDo(ctx?.allowedTherapistSignatureActions, marker) ||
    canDo(ctx?.allowedPhysicianSignatureActions, marker)
  ) {
    return { axis: 'THERAPIST', onBehalfOfProviderId: document?.providerId ?? null };
  }
  if (canDo(ctx?.allowedSupervisorSignatureActions, marker)) {
    return {
      axis: 'SUPERVISOR',
      onBehalfOfProviderId: ctx?.supervisor?.id ?? document?.supervisorId ?? null,
    };
  }
  return null;
}
